import { ExternalLink, GitBranch, Clock } from 'lucide-react';
import type { PrCardData } from '@/components/layout/chat-pane.types';

interface PrCardProps {
  card: PrCardData;
}

/** Render a cloud agent PR/MR card with title, branch, and review link */
export function PrCard({ card }: PrCardProps) {
  const dispatchedAgo = card.dispatchedAt
    ? formatRelativeTime(new Date(card.dispatchedAt))
    : null;

  return (
    <div className="my-2 border border-border rounded-lg p-3 bg-card max-w-sm">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-blue-400">
          <span>🤖</span>
          <span>Cloud Agent PR</span>
        </div>
        {dispatchedAgo && (
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Clock className="w-3 h-3" />
            <span>{dispatchedAgo}</span>
          </div>
        )}
      </div>

      <p className="text-sm font-medium text-foreground mb-2 leading-snug">
        {card.title}
      </p>

      {card.branch && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
          <GitBranch className="w-3 h-3" />
          <code className="bg-muted px-1 py-0.5 rounded text-[11px]">{card.branch}</code>
        </div>
      )}

      <a
        href={card.prUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors"
      >
        <ExternalLink className="w-3 h-3" />
        Review MR / PR →
      </a>
    </div>
  );
}

/** Format a date as relative time (e.g. "5m ago", "2h ago") */
function formatRelativeTime(date: Date): string {
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}
